import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError, api } from '../api/client';
import { AuthLayout } from '../components/AuthLayout';
import { Alert, Field, LoadingBlock } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useResource } from '../hooks/useResource';

/**
 * Lenken den andre i brudeparet får.
 *
 * Tokenet står i spørrestrengen og ikke i stien, av samme grunn som ved
 * tilbakestilling av passord: da havner det aldri i en `Referer`-header hvis
 * siden lenker ut, og «personvernerklæringen» nederst er nettopp en slik lenke.
 *
 * Adressen står låst i skjemaet. Den er ikke et felt man fyller ut – den er
 * hele grunnen til at invitasjonen finnes, og et redigerbart felt der ville
 * gjort lenken til en måte å opprette konto på hvilken som helst adresse. Den
 * vises fordi den som skal skrive et passord, bør se hvilken konto passordet
 * hører til.
 */

type Invitasjon = {
  epost: string;
  navn: string | null;
  invitertAv: string | null;
  brudepar: string;
  harKonto: boolean;
  /** «partner», «toastmaster» eller «forlover». */
  rolle: string;
  /** Satt for hjelperollene, null for partneren. */
  rolleNavn: string | null;
  /** Bryllupet har ikke Plus akkurat nå, så en toastmaster eller forlover ser ingenting ennå. */
  delingPaaPause?: boolean;
};

export function BliMedPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { status, user, refresh, selectWedding } = useAuth();

  const { data, loading, error } = useResource<{ invitasjon: Invitasjon }>(
    () => api.get<{ invitasjon: Invitasjon }>(`/partnerinvitasjon/${token}`),
    [token],
  );

  const [form, setForm] = useState({ name: '', password: '', acceptTerms: false });
  const [feil, setFeil] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!token) {
    return (
      <AuthLayout title="Lenken mangler" subtitle="Adressen ser ikke komplett ut.">
        <Alert>
          Vi fant ingen lenke i adressen. Kopierte du den fra e-posten, kan den ha blitt delt
          over to linjer.
        </Alert>
      </AuthLayout>
    );
  }

  if (loading || status === 'loading') {
    return (
      <AuthLayout title="Et øyeblikk" subtitle="Vi henter invitasjonen.">
        <LoadingBlock />
      </AuthLayout>
    );
  }

  if (error || !data) {
    return (
      <AuthLayout title="Lenken virker ikke" subtitle="Den kan være brukt, angret eller gått ut.">
        <Alert>{error?.message ?? 'Fant ikke invitasjonen.'}</Alert>
        <p className="small">
          Be den som inviterte deg om å sende en ny. Har du allerede en konto, kan du{' '}
          <Link to="/logg-inn">logge inn</Link>.
        </p>
      </AuthLayout>
    );
  }

  const invitasjon = data.invitasjon;
  const fra = invitasjon.invitertAv?.split(' ')[0] ?? 'Den andre';
  // Partneren blir bedt om å bli med; en toastmaster blir spurt om noe
  // bestemt. «Vil ha deg med» er en hyggelig, men uforståelig beskjed når det
  // er en rolle man er spurt om.
  const rolleSmaa = invitasjon.rolleNavn?.toLowerCase() ?? null;
  const spurtOm = rolleSmaa ? `vil ha deg som ${rolleSmaa}` : 'vil ha deg med';
  // Lenken kan ha ligget i innboksen til gratisperioden var over. Å bli med
  // går fortsatt, men siden skal ikke love noe hen ikke får se.
  const pause = invitasjon.delingPaaPause ? (
    <Alert kind="info">
      Delingen er satt på pause fordi bryllupet ikke har Plus. Du kan bli med nå, og ser det
      brudeparet deler med deg når de har Plus.
    </Alert>
  ) : null;

  const bliMed = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setFeil(null);
    try {
      const svar = await api.post<{ weddingId: string }>(
        `/partnerinvitasjon/${token}/godta`,
        // Er man innlogget fra før, er det den kontoen som kobles til, og da
        // finnes det ikke noe skjema å sende.
        status === 'authenticated' ? undefined : form,
      );
      await refresh();
      selectWedding(svar.weddingId);
      navigate('/app?velkommen=1', { replace: true });
    } catch (caught) {
      setFeil(caught instanceof ApiError ? caught.message : 'Klarte ikke å bli med.');
      setBusy(false);
    }
  };

  // Allerede innlogget: ingenting å fylle ut, bare å si ja.
  if (status === 'authenticated') {
    return (
      <AuthLayout
        title={`Bli med på ${invitasjon.brudepar}`}
        subtitle={`${fra} ${spurtOm}.`}
      >
        <Alert>{feil}</Alert>
        {pause}
        <p className="small muted">
          Du er innlogget som <strong>{user?.email}</strong>. Det er denne kontoen som blir
          koblet til bryllupet.
        </p>
        <form onSubmit={bliMed}>
          <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={busy}>
            {busy ? 'Kobler til …' : 'Bli med'}
          </button>
        </form>
      </AuthLayout>
    );
  }

  // Adressen har konto fra før. Da er veien inn innlogging, ikke en ny konto.
  if (invitasjon.harKonto) {
    return (
      <AuthLayout
        title={`Bli med på ${invitasjon.brudepar}`}
        subtitle={`${fra} ${spurtOm}.`}
      >
        {pause}
        <p>
          Det finnes allerede en konto på <strong>{invitasjon.epost}</strong>. Logg inn med den,
          så kommer du rett tilbake hit.
        </p>
        <Link
          className="btn btn-primary btn-block btn-lg"
          to={`/logg-inn?neste=${encodeURIComponent(`/bli-med?token=${token}`)}`}
        >
          Logg inn
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={rolleSmaa ? `${fra} ${spurtOm}` : `${fra} vil ha deg med`}
      subtitle={`Planleggingen av bryllupet til ${invitasjon.brudepar} er i gang.`}
    >
      {pause}
      <p className="small muted">
        {rolleSmaa
          ? invitasjon.delingPaaPause
            ? 'Lag din egen innlogging, så er du klar når brudeparet har Plus.'
            : `Lag din egen innlogging, så ser du det brudeparet har delt med ${rolleSmaa}en. De kan endre det underveis.`
          : 'Lag din egen innlogging, så deler dere alt: gjestelisten, budsjettet, oppgavene og bordkartet.'}
      </p>

      <form onSubmit={bliMed}>
        <Alert>{feil}</Alert>

        <Field label="E-post" hint="Invitasjonen ble sendt hit, og kontoen blir på denne adressen.">
          <input className="input" value={invitasjon.epost} readOnly disabled />
        </Field>

        <Field label="Navnet ditt">
          <input
            className="input"
            required
            autoFocus
            autoComplete="name"
            placeholder={invitasjon.navn ?? ''}
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </Field>

        <Field label="Velg et passord" hint="Minst ti tegn. Gjerne noe langt og enkelt å huske.">
          <input
            className="input"
            type="password"
            required
            minLength={10}
            autoComplete="new-password"
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
          />
        </Field>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.acceptTerms}
            onChange={(event) => setForm({ ...form, acceptTerms: event.target.checked })}
          />
          <span>
            Jeg godtar{' '}
            <Link to="/vilkaar" target="_blank">
              bruksvilkårene
            </Link>{' '}
            og har lest{' '}
            <Link to="/personvern" target="_blank">
              personvernerklæringen
            </Link>
            .
          </span>
        </label>

        <button
          className="btn btn-primary btn-block btn-lg"
          type="submit"
          disabled={busy || !form.acceptTerms}
          style={{ marginTop: '0.5rem' }}
        >
          {busy ? 'Oppretter konto …' : 'Bli med'}
        </button>
      </form>
    </AuthLayout>
  );
}
